import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  DashboardMetrics,
  EezyguardClient,
  STATUS_LABELS,
  EezyguardStatus,
} from 'src/apis/eezyguard';
import SkeletonLoad from 'src/pages/components/SkeletonLoad';
import NoDataAlert from 'src/pages/components/NoDataAlert';
import { EEZYGUARD_TYPE_LABELS } from './labels';
import { STATUS_COLORS } from './common';

const PIE_COLORS = [
  '#0f5132',
  '#3f9fff',
  '#e0a106',
  '#d64545',
  '#7c5cff',
  '#2e9e5b',
  '#7b8794',
  '#12b5b0',
];

type Props = {
  client: EezyguardClient;
  filters?: Record<string, string>;
  onError?: (message: string) => void;
};

function StatTile({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h4" fontWeight={700}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ChartCard({
  title,
  isEmpty,
  children,
}: {
  title: string;
  isEmpty: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Card variant="outlined" sx={{ height: 320 }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {isEmpty ? <NoDataAlert /> : children}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function EezyGuardDashboard({
  client,
  filters,
  onError,
}: Props): JSX.Element {
  const theme = useTheme();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  const filterKey = JSON.stringify(filters ?? {});
  useEffect(() => {
    let active = true;
    setLoading(true);
    client
      .getDashboard(filters ?? {})
      .then((data) => active && setMetrics(data))
      .catch(() => onError?.('Erro ao carregar o dashboard.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const byType = useMemo(
    () =>
      Object.entries(metrics?.by_type ?? {}).map(([key, value]) => ({
        name: EEZYGUARD_TYPE_LABELS[key] ?? key,
        value,
      })),
    [metrics]
  );
  const byStatus = useMemo(
    () =>
      Object.entries(metrics?.by_status ?? {}).map(([key, value]) => ({
        name: STATUS_LABELS[key as EezyguardStatus] ?? key,
        key,
        value,
      })),
    [metrics]
  );
  const bySector = useMemo(
    () =>
      Object.entries(metrics?.by_sector ?? {}).map(([key, value]) => ({
        name: key,
        value,
      })),
    [metrics]
  );

  if (loading) return <SkeletonLoad variant="cards" rows={3} />;
  if (!metrics) return <NoDataAlert />;

  const axisColor = theme.palette.text.secondary;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container spacing={2}>
        {[
          { label: 'Total de ocorrências', value: metrics.totals.total },
          { label: 'Casos em aberto', value: metrics.totals.open },
          { label: 'Concluídos', value: metrics.totals.completed },
          { label: 'Casos críticos', value: metrics.totals.critical },
          { label: 'Aguardando análise', value: metrics.totals.awaiting_review },
          { label: 'Não encaminhados', value: metrics.totals.not_forwarded },
        ].map((tile) => (
          <Grid key={tile.label} item xs={6} sm={4} md={2}>
            <StatTile label={tile.label} value={tile.value} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Ocorrências por tipo" isEmpty={byType.length === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={byType}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                >
                  {byType.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Incidência ao longo do tempo"
            isEmpty={metrics.timeline.length === 0}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics.timeline}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" stroke={axisColor} fontSize={12} />
                <YAxis stroke={axisColor} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#0f5132" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Ocorrências por status" isEmpty={byStatus.length === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byStatus}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" stroke={axisColor} fontSize={11} />
                <YAxis stroke={axisColor} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value">
                  {byStatus.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={STATUS_COLORS[entry.key as EezyguardStatus] ?? '#3f9fff'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Ocorrências por setor" isEmpty={bySector.length === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySector} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" stroke={axisColor} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={11} width={90} />
                <Tooltip />
                <Bar dataKey="value" fill="#7c5cff" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
